import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Send, CheckCircle2, Lock, Unlock, ListChecks, Download, MessageSquarePlus, Trash2, Plus, X, Save } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { StatCard } from "@/components/common/StatCard";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { SnapshotSection } from "./WeeklyReviewSnapshot";
import { WR_SECTIONS, WR_OWNERS, WR_PRIORITIES, wrStatusBadge, weekRange, fmtDate, gbp, readinessTrend } from "@/data/weeklyReviewOptions";

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-xs font-semibold text-[#5A6B8C]">{label}</Label>{children}</div>
);

export const WeeklyReviewDetail = ({ id, onBack, onChanged }) => {
  const { canWrite, isAdmin } = useUser();
  const [rev, setRev] = useState(null);
  const [form, setForm] = useState({});
  const [notes, setNotes] = useState({});
  const [nwp, setNwp] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmFinal, setConfirmFinal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  const [prioPreview, setPrioPreview] = useState(false);
  const [commentFor, setCommentFor] = useState(null);
  const [commentText, setCommentText] = useState("");

  const load = useCallback(async () => {
    const r = await api.weeklyReview(id);
    setRev(r);
    setForm({ overall_assessment: r.overall_assessment || "", main_achievements: r.main_achievements || "", main_concerns: r.main_concerns || "", founder_notes: r.founder_notes || "", review_meeting_date: r.review_meeting_date || "", attendees: r.attendees || "" });
    setNotes(r.section_notes || {});
    setNwp(r.next_week_priorities || []);
    setDirty(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!rev) return <div data-testid="wr-detail-loading" className="py-12 text-center text-sm text-[#5A6B8C]">Loading review…</div>;

  const finalised = rev.status === "Finalised";
  const editable = canWrite && !finalised;
  const snap = rev.snapshot || {};
  const ov = snap.overview || {};
  const trend = readinessTrend(ov.readiness_change);

  const setF = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const setNote = (key, field, v) => { setNotes((n) => ({ ...n, [key]: { ...(n[key] || {}), [field]: v } })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const u = await api.updateWeeklyReview(id, { ...form, section_notes: notes, next_week_priorities: nwp });
      setRev(u); setDirty(false); toast.success("Review saved"); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };

  const doRefresh = async () => {
    try { const u = await api.refreshWeeklyReview(id); setRev(u); setConfirmRefresh(false); toast.success("Snapshot refreshed with live data"); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not refresh"); }
  };

  const transition = async (status, confirm = false) => {
    try {
      const u = await api.weeklyReviewTransition(id, status, confirm);
      setRev(u); setConfirmFinal(false);
      toast.success(status === "Finalised" ? "Review finalised" : `Marked ${status}`);
      onChanged?.();
      if (status === "Finalised" && (u.next_week_priorities || []).length && !u.priorities_created) setPrioPreview(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
  };

  const doDelete = async (reason) => {
    try { await api.deleteWeeklyReview(id, reason); toast.success("Review moved to Recycle Bin"); setConfirmDelete(false); onChanged?.(); onBack(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const createPriorities = async () => {
    try {
      const res = await api.weeklyReviewCreatePriorities(id);
      toast.success(`Created ${res.created.length} priorities${res.skipped.length ? `, skipped ${res.skipped.length} duplicate(s)` : ""}`);
      setPrioPreview(false); await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create priorities"); }
  };

  const submitComment = async () => {
    if (!commentText.trim()) { toast.error("Comment text is required"); return; }
    try { const u = await api.weeklyReviewComment(id, commentFor, commentText.trim()); setRev(u); setCommentFor(null); setCommentText(""); toast.success("Comment added"); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add comment"); }
  };

  const addNwp = () => { if (nwp.length >= 5) { toast.error("Up to five priorities only"); return; } setNwp([...nwp, { title: "", description: "", owner: "Steven", due_date: "", priority: "Medium", success_measure: "" }]); setDirty(true); };
  const setNwpF = (i, k, v) => { setNwp(nwp.map((p, idx) => (idx === i ? { ...p, [k]: v } : p))); setDirty(true); };
  const rmNwp = (i) => { setNwp(nwp.filter((_, idx) => idx !== i)); setDirty(true); };

  const openPrint = () => window.open(`/weekly-review/${id}/print`, "_blank");

  const toggleTasks = async (next) => {
    try { const u = await api.updateWeeklyReview(id, { include_tasks: next }); setRev(u); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const tsk = snap.tasks || {};

  return (
    <div className="space-y-5" data-testid="wr-detail">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button data-testid="wr-back" onClick={onBack} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E4E9F3] text-[#5A6B8C] transition-colors hover:bg-[#F7F9FE]"><ArrowLeft size={17} /></button>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#1D4ED8]">Founders' Weekly Review</p>
            <h2 className="font-heading text-xl font-bold text-[#14245F]">Week commencing {weekRange(rev.week_start, rev.week_end)}</h2>
          </div>
          <StatusBadge status={wrStatusBadge(rev.status)} label={rev.status} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" data-testid="wr-download-pdf" onClick={openPrint} title="Opens a print-ready view — choose 'Save as PDF' in the print dialog" className="border-[#E4E9F3] text-[#5A6B8C]"><Download size={14} /> Download PDF</Button>
          {editable && rev.status === "Draft" && <Button size="sm" variant="outline" data-testid="wr-refresh" onClick={() => setConfirmRefresh(true)} className="border-[#E4E9F3] text-[#1D4ED8]"><RefreshCw size={14} /> Refresh</Button>}
          {dirty && editable && <Button size="sm" data-testid="wr-save" onClick={save} disabled={saving} className="bg-[#1D4ED8] text-white hover:bg-[#1E40AF]"><Save size={14} /> Save</Button>}
        </div>
      </div>

      {/* Overview strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard testid="wr-ov-readiness" label="Launch Readiness" value={`${ov.launch_readiness ?? 0}%`} hint={trend.label} tone="orange" index={0} />
        <StatCard testid="wr-ov-completed" label="Priorities Completed" value={ov.priorities_completed ?? 0} tone="jungle" index={1} />
        <StatCard testid="wr-ov-overdue" label="Overdue Items" value={ov.overdue_items ?? 0} tone="coral" index={2} />
        <StatCard testid="wr-ov-blockers" label="Open Blockers" value={ov.open_blockers ?? 0} tone="gold" index={3} />
        <StatCard testid="wr-ov-critical" label="Critical Risks" value={ov.critical_risks ?? 0} tone="magenta" index={4} />
        <StatCard testid="wr-ov-cash" label="Available Cash" value={gbp(ov.available_cash)} tone="teal" index={5} />
        <StatCard testid="wr-ov-runway" label="Estimated Runway" value={ov.runway_label || "—"} tone="royal" index={6} />
        <StatCard testid="wr-ov-followups" label="Investor Follow-ups Due" value={ov.investor_followups_due ?? 0} tone="purple" index={7} />
      </div>

      {/* Review meta + assessment */}
      <SectionCard testid="wr-assessment" accent="#1D4ED8" title="Weekly Assessment" subtitle={`Prepared by ${rev.prepared_by || "—"}`}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Field label="Overall weekly assessment *"><Textarea data-testid="wr-overall" value={form.overall_assessment} onChange={(e) => setF("overall_assessment", e.target.value)} rows={3} disabled={!editable} className="border-[#E4E9F3]" placeholder="How did the week go overall?" /></Field>
          <Field label="Founder notes"><Textarea data-testid="wr-notes" value={form.founder_notes} onChange={(e) => setF("founder_notes", e.target.value)} rows={3} disabled={!editable} className="border-[#E4E9F3]" /></Field>
          <Field label="Main achievements"><Textarea data-testid="wr-achievements" value={form.main_achievements} onChange={(e) => setF("main_achievements", e.target.value)} rows={2} disabled={!editable} className="border-[#E4E9F3]" /></Field>
          <Field label="Main concerns"><Textarea data-testid="wr-concerns" value={form.main_concerns} onChange={(e) => setF("main_concerns", e.target.value)} rows={2} disabled={!editable} className="border-[#E4E9F3]" /></Field>
          <Field label="Review meeting date"><Input type="date" data-testid="wr-meeting-date" value={(form.review_meeting_date || "").slice(0, 10)} onChange={(e) => setF("review_meeting_date", e.target.value)} disabled={!editable} className="border-[#E4E9F3]" /></Field>
          <Field label="Attendees"><Input data-testid="wr-attendees" value={form.attendees} onChange={(e) => setF("attendees", e.target.value)} disabled={!editable} className="border-[#E4E9F3]" placeholder="Steven, Richard" /></Field>
        </div>
      </SectionCard>

      {/* Snapshot sections with per-section commentary */}
      {WR_SECTIONS.map((sec) => {
        const n = notes[sec.key] || {};
        const secComments = (rev.comments || []).filter((c) => c.section === sec.key);
        return (
          <SectionCard key={sec.key} testid={`wr-section-${sec.key}`} accent={sec.accent} icon={sec.icon} title={sec.label}
            action={!finalised && canWrite && <button data-testid={`wr-comment-btn-${sec.key}`} onClick={() => { setCommentFor(sec.key); setCommentText(""); }} className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ backgroundColor: sec.accent }}><MessageSquarePlus size={13} /> Comment</button>}>
            <SnapshotSection sectionKey={sec.key} snap={snap} />

            {/* Commentary */}
            <div className="mt-4 grid grid-cols-1 gap-3 rounded-xl border border-dashed border-[#D8E0F0] bg-[#FAFBFF] p-3 lg:grid-cols-2">
              <Field label="What went well"><Textarea data-testid={`wr-note-well-${sec.key}`} value={n.went_well || ""} onChange={(e) => setNote(sec.key, "went_well", e.target.value)} rows={2} disabled={!editable} className="border-[#E4E9F3] bg-white" /></Field>
              <Field label="What did not go well"><Textarea data-testid={`wr-note-badly-${sec.key}`} value={n.went_badly || ""} onChange={(e) => setNote(sec.key, "went_badly", e.target.value)} rows={2} disabled={!editable} className="border-[#E4E9F3] bg-white" /></Field>
              <Field label="Decision or action required"><Textarea data-testid={`wr-note-action-${sec.key}`} value={n.action_required || ""} onChange={(e) => setNote(sec.key, "action_required", e.target.value)} rows={2} disabled={!editable} className="border-[#E4E9F3] bg-white" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Owner">
                  <Select value={n.action_owner || ""} onValueChange={(v) => setNote(sec.key, "action_owner", v)} disabled={!editable}>
                    <SelectTrigger data-testid={`wr-note-owner-${sec.key}`} className="border-[#E4E9F3] bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{WR_OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Deadline"><Input type="date" data-testid={`wr-note-deadline-${sec.key}`} value={(n.action_deadline || "").slice(0, 10)} onChange={(e) => setNote(sec.key, "action_deadline", e.target.value)} disabled={!editable} className="border-[#E4E9F3] bg-white" /></Field>
              </div>
            </div>

            {secComments.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid={`wr-comments-${sec.key}`}>
                {secComments.map((c) => (
                  <div key={c.id} className="rounded-lg bg-[#EAF0FF] px-3 py-2 text-xs text-[#14245F]">
                    <span className="font-bold text-[#1D4ED8]">{c.author}</span> <span className="text-[#5A6B8C]">· {fmtDate(c.created_at)}</span><br />{c.text}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        );
      })}

      {/* Optional daily-tasks pull-in */}
      <SectionCard testid="wr-tasks" accent="#06B6D4" icon={ListChecks} title="This Week's Tasks"
        subtitle="Optionally include completed and outstanding daily tasks in this review"
        action={<label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-[#5A6B8C]">
          <Switch data-testid="wr-include-tasks" checked={!!rev.include_tasks} onCheckedChange={toggleTasks} disabled={!editable} />
          Include in review
        </label>}>
        {!rev.include_tasks ? (
          <p className="text-sm text-[#94A3C4]">Tasks are not included in this review. Toggle “Include in review” to add this week's completed and outstanding tasks (also appears in the PDF).</p>
        ) : (
          <div className="space-y-4" data-testid="wr-tasks-body">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard testid="wr-tasks-completed" label="Completed this week" value={tsk.completed_count ?? 0} tone="jungle" index={0} />
              <StatCard testid="wr-tasks-outstanding" label="Outstanding" value={tsk.outstanding_count ?? 0} tone="royal" index={1} />
              <StatCard testid="wr-tasks-blocked" label="Blocked" value={tsk.blocked_count ?? 0} tone="magenta" index={2} />
              <StatCard testid="wr-tasks-overdue" label="Overdue" value={tsk.overdue_count ?? 0} tone="coral" index={3} />
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#16A34A]">Completed ({(tsk.completed || []).length})</p>
                {(tsk.completed || []).length === 0 ? <p className="text-sm text-[#94A3C4]">None completed this week.</p> : (
                  <ul className="space-y-1.5">{tsk.completed.map((t) => (
                    <li key={t.id} className="rounded-lg border border-[#E4E9F3] bg-[#F7F9FE] px-3 py-1.5 text-sm text-[#14245F]">
                      <span className="font-semibold">{t.title}</span> <span className="text-xs text-[#5A6B8C]">· {t.assigned_to} · {t.workstream}</span>
                    </li>))}
                  </ul>
                )}
              </div>
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#1D4ED8]">Outstanding ({(tsk.outstanding || []).length})</p>
                {(tsk.outstanding || []).length === 0 ? <p className="text-sm text-[#94A3C4]">Nothing outstanding.</p> : (
                  <ul className="space-y-1.5">{tsk.outstanding.map((t) => (
                    <li key={t.id} className="rounded-lg border border-[#E4E9F3] bg-white px-3 py-1.5 text-sm text-[#14245F]">
                      <span className="font-semibold">{t.title}</span> <span className="text-xs text-[#5A6B8C]">· {t.assigned_to} · {t.status}{t.due_date ? ` · due ${t.due_date}` : ""}</span>
                    </li>))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </SectionCard>

      {/* Next-week priorities */}
      <SectionCard testid="wr-nwp" accent="#7C3AED" icon={ListChecks} title="Agreed Priorities for Next Week" subtitle="Up to five priorities — create them as Command Centre priorities when you finalise"
        action={editable && nwp.length < 5 && <Button size="sm" data-testid="wr-add-nwp" onClick={addNwp} className="bg-[#7C3AED] text-white hover:bg-[#6D28D9]"><Plus size={14} /> Add priority</Button>}>
        {nwp.length === 0 ? <p className="text-sm text-[#94A3C4]">No priorities agreed yet. Add up to five for the coming week.</p> : (
          <div className="space-y-3">
            {nwp.map((p, i) => (
              <div key={p.id || i} data-testid={`wr-nwp-row-${i}`} className="rounded-xl border border-[#E4E9F3] bg-[#F7F5FF] p-3">
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <Input data-testid={`wr-nwp-title-${i}`} value={p.title} onChange={(e) => setNwpF(i, "title", e.target.value)} disabled={!editable} placeholder="Priority title" className="border-[#E4E9F3] bg-white font-semibold" />
                    <Textarea data-testid={`wr-nwp-desc-${i}`} value={p.description} onChange={(e) => setNwpF(i, "description", e.target.value)} disabled={!editable} rows={2} placeholder="Description" className="border-[#E4E9F3] bg-white" />
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <Select value={p.owner} onValueChange={(v) => setNwpF(i, "owner", v)} disabled={!editable}><SelectTrigger data-testid={`wr-nwp-owner-${i}`} className="border-[#E4E9F3] bg-white"><SelectValue /></SelectTrigger><SelectContent>{WR_OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                      <Select value={p.priority} onValueChange={(v) => setNwpF(i, "priority", v)} disabled={!editable}><SelectTrigger data-testid={`wr-nwp-prio-${i}`} className="border-[#E4E9F3] bg-white"><SelectValue /></SelectTrigger><SelectContent>{WR_PRIORITIES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                      <Input type="date" data-testid={`wr-nwp-due-${i}`} value={(p.due_date || "").slice(0, 10)} onChange={(e) => setNwpF(i, "due_date", e.target.value)} disabled={!editable} className="border-[#E4E9F3] bg-white" />
                      <Input data-testid={`wr-nwp-measure-${i}`} value={p.success_measure} onChange={(e) => setNwpF(i, "success_measure", e.target.value)} disabled={!editable} placeholder="Success measure" className="border-[#E4E9F3] bg-white" />
                    </div>
                  </div>
                  {editable && <button data-testid={`wr-nwp-remove-${i}`} onClick={() => rmNwp(i)} className="text-[#DC2626] hover:opacity-70"><Trash2 size={16} /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
        {rev.status === "Finalised" && (rev.next_week_priorities || []).length > 0 && (
          <div className="mt-3">
            {rev.priorities_created ? <p className="text-xs font-semibold text-[#16A34A]">✓ Priorities created in Command Centre</p>
              : isAdmin || canWrite ? <Button size="sm" data-testid="wr-create-priorities" onClick={() => setPrioPreview(true)} className="bg-[#7C3AED] text-white hover:bg-[#6D28D9]"><ListChecks size={14} /> Create these priorities</Button> : null}
          </div>
        )}
      </SectionCard>

      {/* Workflow actions */}
      <SectionCard testid="wr-workflow" accent="#0EA5A4" title="Review Workflow" subtitle={`Current status: ${rev.status}`}>
        <div className="flex flex-wrap gap-2">
          {editable && rev.status === "Draft" && <Button data-testid="wr-mark-ready" onClick={() => transition("Ready for review")} className="bg-[#E0A82E] text-white hover:bg-[#C2410C]"><Send size={15} /> Mark ready for review</Button>}
          {editable && rev.status === "Ready for review" && <Button data-testid="wr-mark-reviewed" onClick={() => transition("Reviewed")} className="bg-[#1D4ED8] text-white hover:bg-[#1E40AF]"><CheckCircle2 size={15} /> Mark reviewed</Button>}
          {editable && (rev.status === "Reviewed" || rev.status === "Ready for review" || rev.status === "Draft") && <Button data-testid="wr-finalise" onClick={() => setConfirmFinal(true)} className="bg-[#16A34A] text-white hover:bg-[#15803D]"><Lock size={15} /> Finalise review</Button>}
          {finalised && isAdmin && <Button data-testid="wr-reopen" onClick={() => transition("Reviewed")} variant="outline" className="border-[#E0A82E] text-[#A16207]"><Unlock size={15} /> Reopen (admin)</Button>}
          {isAdmin && rev.status === "Draft" && <Button data-testid="wr-delete" onClick={() => setConfirmDelete(true)} variant="outline" className="border-[#DC2626] text-[#DC2626]"><Trash2 size={15} /> Delete draft</Button>}
        </div>
        {finalised && <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[#16A34A]"><Lock size={13} /> Finalised on {fmtDate(rev.finalised_at)} — locked from edits (an administrator can reopen it).</p>}
      </SectionCard>

      {/* Comment dialog */}
      <Dialog open={!!commentFor} onOpenChange={(o) => !o && setCommentFor(null)}>
        <DialogContent data-testid="wr-comment-dialog" className="max-w-md border-[#E4E9F3] bg-white">
          <DialogHeader><DialogTitle className="font-heading text-[#14245F]">Add comment</DialogTitle><DialogDescription>Your comment is attributed to your account.</DialogDescription></DialogHeader>
          <Textarea data-testid="wr-comment-text" value={commentText} onChange={(e) => setCommentText(e.target.value)} rows={4} className="border-[#E4E9F3]" placeholder="Share your view on this section…" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommentFor(null)} className="border-[#E4E9F3]">Cancel</Button>
            <Button data-testid="wr-comment-submit" onClick={submitComment} className="bg-[#1D4ED8] text-white hover:bg-[#1E40AF]">Add comment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finalise confirm */}
      <Dialog open={confirmFinal} onOpenChange={setConfirmFinal}>
        <DialogContent data-testid="wr-finalise-dialog" className="max-w-md border-[#E4E9F3] bg-white">
          <DialogHeader><DialogTitle className="font-heading text-[#14245F]">Finalise this review?</DialogTitle><DialogDescription>Finalising locks the review from further edits. An overall assessment and at least one agreed next-week priority are required. Only an administrator can reopen it afterwards.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmFinal(false)} className="border-[#E4E9F3]">Cancel</Button>
            <Button data-testid="wr-finalise-confirm" onClick={() => transition("Finalised", true)} className="bg-[#16A34A] text-white hover:bg-[#15803D]"><Lock size={15} /> Finalise</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refresh confirm */}
      <Dialog open={confirmRefresh} onOpenChange={setConfirmRefresh}>
        <DialogContent data-testid="wr-refresh-dialog" className="max-w-md border-[#E4E9F3] bg-white">
          <DialogHeader><DialogTitle className="font-heading text-[#14245F]">Refresh the draft snapshot?</DialogTitle><DialogDescription>This replaces the captured figures with the latest live War Room data. Your written notes, commentary and next-week priorities are kept. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRefresh(false)} className="border-[#E4E9F3]">Cancel</Button>
            <Button data-testid="wr-refresh-confirm" onClick={doRefresh} className="bg-[#1D4ED8] text-white hover:bg-[#1E40AF]"><RefreshCw size={15} /> Refresh snapshot</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Priorities preview */}
      <Dialog open={prioPreview} onOpenChange={setPrioPreview}>
        <DialogContent data-testid="wr-prio-preview" className="max-w-lg border-[#E4E9F3] bg-white">
          <DialogHeader><DialogTitle className="font-heading text-[#14245F]">Create Command Centre priorities</DialogTitle><DialogDescription>These agreed priorities will be added to the Command Centre. Duplicates (same title as an active priority) are skipped automatically.</DialogDescription></DialogHeader>
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {(rev.next_week_priorities || []).map((p, i) => (
              <div key={`${p.title || "untitled"}-${i}`} className="rounded-lg border border-[#E4E9F3] px-3 py-2 text-sm">
                <p className="font-semibold text-[#14245F]">{p.title || <span className="text-[#DC2626]">(no title — will be skipped)</span>}</p>
                <p className="text-xs text-[#5A6B8C]">{p.owner} · {p.priority}{p.due_date ? ` · due ${p.due_date}` : ""}</p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrioPreview(false)} className="border-[#E4E9F3]">Not now</Button>
            <Button data-testid="wr-prio-confirm" onClick={createPriorities} className="bg-[#7C3AED] text-white hover:bg-[#6D28D9]"><ListChecks size={15} /> Create priorities</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={confirmDelete} onOpenChange={setConfirmDelete} title="Delete this draft review?" onConfirm={doDelete} softDelete />
    </div>
  );
};
